import { useEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import * as Yup from 'yup';
import cn from 'classnames';
import { Field, Form, FormikProvider, useFormik } from 'formik';

import { useUserContext, useClientContext, useTranslationContext } from 'hooks';
import { TrackerServiceInstance, UserServiceInstance } from 'services';
import * as interfaces from 'interfaces';

import Icons from 'shared/Icons';

import classes from './SubmitLikeDropdown.module.scss';

function useOutsideAlerter(ref: any, handleClose: () => void) {
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        handleClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClose, ref]);
}

export default function SubmitLikeDropdown(props: {
  isOpen: boolean;
  handleClose: () => void;
  handleClick: () => void;
  hasLikedVideo: boolean;
}) {
  const { isOpen, handleClose, handleClick, hasLikedVideo } = props;
  const { user: currentUser, updateUser, setIsSignedIn } = useUserContext();
  const { client } = useClientContext();
  const { t } = useTranslationContext();
  const [onNewUserFlow, setOnNewUserFlow] = useState(false);
  const wrapperRef = useRef(null);

  const [isUserOldVisitor, setIsUserOldVisitor] = useState(false);

  useOutsideAlerter(wrapperRef, () => handleClose());

  const validationSchema = Yup.object().shape({
    email: Yup.string().required(t("Please enter your email")).email(t("Please enter valid email")),
    firstName: onNewUserFlow ? Yup.string().required(t("Please add your name")) : Yup.string(),
    zip: onNewUserFlow ? Yup.string().required(t('Please add zip code')) : Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      email: currentUser.email || '',
      firstName: currentUser.firstName || '',
      lastName: currentUser.lastName || '',
      zip: currentUser.zip || '',
    },
    validationSchema,
    onSubmit: (values, actions) => {
      if (!onNewUserFlow) {
        if (values.email) {
          UserServiceInstance?.getUserByEmail(client.id, values.email).then((res: any) => {
            if (res.code === "ERROR_USER_NOT_VERIFIED") {
              toast.error(t("We are not able to associate this email with your activity"));
              return;
            }

            if (!res.data) {
              setOnNewUserFlow(true);
              actions.setTouched({});
            }
            else {

              const response = (res.data || [])[0];

              UserServiceInstance?.storeUserAndSessionData(response.user, response.token);
              TrackerServiceInstance?.setAuthorizationHeader(response.token);

              updateUser({...response.user, clientId: client.id});

              if (['user', 'admin'].includes(response.user.accessLevel)) {
                setIsSignedIn(true);
                UserServiceInstance?.sendMagicLink(response.user.email, client.id, true).then( () =>
                    toast.success(t("Check your Email for Confirmation link"))
                );
                setTimeout(() => handleClick(), 250);
                handleClose();
              } else {
                setOnNewUserFlow(true);
                setIsUserOldVisitor( true);
              }
            }
          })
        } else
          toast.error(t("Please enter your email"));
      }

      if (onNewUserFlow) {
        if ( values.email && values.firstName && values.zip){
          const payload : {
            email?: string;
            zip: string;
            firstName: string;
            lastName?: string;
          } = {firstName: values.firstName, zip: values.zip};

          if (!isUserOldVisitor)
            payload.email = values.email;

          if (values.lastName)
            payload.lastName = values.lastName;

          UserServiceInstance?.updateCurrentUserSession(payload).then((receivedUser: interfaces.SessionDataInterface) => {
            if (receivedUser.email) {
              UserServiceInstance?.sendMagicLink(receivedUser.email, client.id, true).then( () =>
                toast.success(t("Check your Email for Confirmation link"))
              );

              updateUser({...currentUser,
                email: receivedUser.email,
                zip: receivedUser.zip,
                firstName: receivedUser.firstName,
                lastName: receivedUser.lastName,
                accessLevel: receivedUser.accessLevel,
                clientId: client.id
              });

              setIsSignedIn(true);
              setTimeout(() => handleClick(), 250);
              handleClose();
            } else
              toast.error(t("There is something wrong with email update"));
          });
        } else
          toast.error(t("Please fill in Email, Zip Code and First Name"));
      }
    }
  });

  const { touched, errors } = formik;

  const clientName = client.id == '209' ? `${( client?.name || 'Candidate' )}` : `${client?.firstName || 'Candidate'}`

  return isOpen ? (
    <div className={cn(classes.toolTip, 'toolTip')} ref={wrapperRef}>
      <div className={classes.wrapper}>
        <div className={classes.submitLikeDropdown}>
          <div className={classes.subTitle}>
            <Icons iconType="bullhorn" iconClass={classes.bullhorn} />

            {hasLikedVideo?
              <div>{t("Get the latest answers from") } {clientName}.</div>
            :
              <div>{t("Get notified when") } {clientName} {t("answers your question")}!</div>
            }

          </div>

          <FormikProvider value={formik}>
            <Form>
              <div className={classes.dataInputWrapper}>
                <Field
                  type="email"
                  name="email"
                  placeholder={t("Please enter your email")}
                  className={`${classes.dataInput} ${errors.email && touched.email && classes.hasError}`}
                />

                {errors.email && touched.email && <div className={classes.errorText}>{errors.email}</div>}

                {onNewUserFlow && <>
                  <Field
                    type="text"
                    name="zip"
                    placeholder={t("Enter your zip code")}
                    className={`${classes.dataInput} ${errors.zip && touched.zip && classes.hasError}`}
                  />

                  {errors.zip && touched.zip && <div className={classes.errorText}>{errors.zip}</div>}

                  <Field
                    type="text"
                    name="firstName"
                    placeholder={t("Enter your name")}
                    className={`${classes.dataInput} ${errors.firstName && touched.firstName && classes.hasError}`}
                  />

                  {errors.firstName && touched.firstName && <div className={classes.errorText}>{errors.firstName}</div>}

                  <Field
                    type="text"
                    name="lastName"
                    placeholder={t("Enter your last name (optional)")}
                    className={classes.dataInput}
                  />
                </>}

              </div>

              <button className={classes.okButton} type="submit">Ok</button>
            </Form>
          </FormikProvider>
        </div>
      </div>

      <ToastContainer position="top-center" />
    </div>
  ) : null;
}
